<template>
  <div v-if="!normalized_form_sections.length" class="verification-manual-info-card application-details-review__empty">
    <div class="text-muted">No submitted applicant form answers were returned by the backend.</div>
  </div>
  <div v-else-if="uses_accordion_layout" class="accordion" :id="accordion_root_id">
    <div
      v-for="accordion_section in accordion_sections"
      :key="accordion_section.key"
      class="accordion-item border rounded-3 overflow-hidden mb-3"
    >
      <h2 class="accordion-header" :id="accordion_header_id(accordion_section)">
        <button
          type="button"
          :class="['accordion-button fw-semibold', is_accordion_open(accordion_section.key) ? '' : 'collapsed']"
          @click="toggle_accordion(accordion_section.key)"
        >
          <span>{{ accordion_section.label }}</span>
          <span
            v-if="accordion_section.form_name && accordion_section.form_name !== accordion_section.label"
            class="badge text-bg-light border ms-2"
          >
            {{ accordion_section.form_name }}
          </span>
        </button>
      </h2>

      <div v-show="is_accordion_open(accordion_section.key)" class="accordion-collapse show">
        <div class="accordion-body bg-white">
          <article
            v-for="sub_section in accordion_section.section.sub_sections"
            :key="sub_section.id"
            class="mb-4"
          >
            <div
              v-if="should_show_sub_section_title(sub_section, accordion_section.section)"
              class="text-uppercase text-muted fw-semibold small mb-3"
            >
              {{ sub_section.label }}
            </div>

            <div v-if="sub_section_has_document_answers(sub_section)" class="d-grid gap-2">
              <div
                v-for="question in sub_section.questions"
                :key="question.id"
                class="d-flex align-items-center justify-content-between gap-3 border rounded-3 px-3 py-2"
              >
                <div class="min-w-0">
                  <div class="fw-semibold text-black">{{ question.label }}</div>
                  <div class="small text-muted text-truncate">
                    {{ question_document_label(question) }}
                  </div>
                </div>
                <i class="bi bi-file-earmark-text text-muted" aria-hidden="true"></i>
              </div>
            </div>

            <div v-else class="row g-3">
              <div
                v-for="question in sub_section.questions"
                :key="question.id"
                class="col-12 col-lg-6"
              >
                <div class="border rounded-3 h-100 px-3 py-2 bg-light">
                  <div class="text-uppercase text-muted fw-semibold small mb-2">{{ question.label }}</div>
                  <div :class="has_answer(question) ? 'fw-semibold text-black' : 'text-muted fst-italic'">
                    {{ format_detail_value(question.answer) }}
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="uses_tabs_layout" class="application-details-review-tabs">
    <nav class="application-details-review-tabs__nav" aria-label="Applicant form sections">
      <button
        v-for="section_tab in flattened_section_tabs"
        :key="section_tab.key"
        type="button"
        :class="['application-details-review-tabs__button', { 'is-active': active_section_key === section_tab.key }]"
        @click="active_section_key = section_tab.key"
      >
        <i class="bi bi-list-check" aria-hidden="true"></i>
        <span>{{ section_tab.label }}</span>
      </button>
    </nav>

    <section v-if="active_section_tab" class="application-details-review-tabs__panel">
      <article
        v-for="sub_section in active_section_tab.section.sub_sections"
        :key="sub_section.id"
        class="application-details-review-tabs__subsection"
      >
        <div
          v-if="should_show_sub_section_title(sub_section, active_section_tab.section)"
          class="application-details-review-tabs__subsection-title"
        >
          {{ sub_section.label }}
        </div>

        <div class="application-details-review-tabs__answer-grid">
          <div
            v-for="question in sub_section.questions"
            :key="question.id"
            :class="['application-details-review-tabs__answer', { 'is-empty': !has_answer(question) }]"
          >
            <span>{{ question.label }}</span>
            <strong>{{ format_detail_value(question.answer) }}</strong>
          </div>
        </div>
      </article>
    </section>
  </div>
  <div v-else class="verification-manual-applicant-review application-details-review">
    <aside class="verification-manual-applicant-nav application-details-review__nav">
      <div class="verification-manual-applicant-nav__title">Forms & Sections</div>
      <div
        v-for="form_section in normalized_form_sections"
        :key="form_section.id"
        class="verification-manual-applicant-nav__form"
      >
        <button
          type="button"
          :class="[
            'verification-manual-applicant-nav__form-title',
            { 'is-collapsed': !is_form_expanded(form_section) }
          ]"
          :aria-expanded="is_form_expanded(form_section)"
          @click="toggle_form(form_section)"
        >
          <i class="bi bi-chevron-down verification-manual-applicant-nav__chevron"></i>
          <span>{{ form_section.form_name }}</span>
        </button>
        <button
          v-for="section in form_section.sections"
          v-show="is_form_expanded(form_section)"
          :key="section.id"
          type="button"
          :class="[
            'verification-manual-applicant-nav__section',
            { 'is-active': is_active_section(form_section, section) }
          ]"
          @click="set_active_section(form_section, section)"
        >
          <span class="verification-manual-applicant-nav__section-label">{{ section.label }}</span>
          <span :class="['verification-manual-applicant-nav__status', `is-${section_status_tone(section)}`]">
            {{ section_status(section) }}
          </span>
        </button>
      </div>
    </aside>

    <div class="verification-manual-applicant-panels application-details-review__panels">
      <template
        v-for="form_section in normalized_form_sections"
        :key="`panels-${form_section.id}`"
      >
        <section
          v-for="section in form_section.sections"
          v-show="is_active_section(form_section, section)"
          :key="section.id"
          class="verification-manual-applicant-panel application-details-review__panel"
        >
          <article
            v-for="sub_section in section.sub_sections"
            :key="sub_section.id"
            class="verification-manual-applicant-subsection application-details-review__subsection"
          >
            <div class="verification-manual-applicant-subsection__header application-details-review__subsection-header">
              <div>
                <h3>{{ sub_section.label || section.label }}</h3>
                <p>Applicant-submitted answers</p>
              </div>
              <span>{{ sub_section_field_count(sub_section) }} fields</span>
            </div>
            <div class="verification-manual-applicant-answer-grid application-details-review__answer-grid">
              <div
                v-for="question in sub_section.questions"
                :key="question.id"
                :class="[
                  'verification-manual-applicant-answer',
                  'application-details-review__answer',
                  { 'is-empty': !has_answer(question) }
                ]"
              >
                <span>{{ question.label }}</span>
                <strong>{{ format_detail_value(question.answer) }}</strong>
              </div>
            </div>
          </article>
        </section>
      </template>
    </div>
  </div>
</template>

<script src="./application_details_review_component.js"></script>

<style src="./ApplicationDetailsReview.css"></style>
